import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Globe, CheckCircle2, XCircle, Copy, ExternalLink, Plus, Trash2, Search, Sparkles } from "lucide-react";

const PASOS = [
  "Elige el nombre de tu tienda: será tu dirección única en90minutos/tu-nombre (no se repite).",
  "Haz clic en la imagen de cualquier producto de la Exposición para agregarlo a tu tienda.",
  "Ajusta tu precio de venta (nunca menor al precio base) y tu ganancia es la diferencia.",
  "Comparte tu link en WhatsApp, Instagram o TikTok: tus clientes te piden directo.",
];

export const NeniTiendaTab = ({ catalogo }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ slug: "", nombre_tienda: "", descripcion: "", whatsapp: "", color: "#FF3366" });
  const [disp, setDisp] = useState(null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const base = window.location.origin.replace(/^https?:\/\//, "");

  const load = () => api.get("/neni/tienda").then(r => {
    setData(r.data);
    const t = r.data.tienda;
    if (t.slug) setForm({ slug: t.slug, nombre_tienda: t.nombre_tienda, descripcion: t.descripcion, whatsapp: t.whatsapp, color: t.color || "#FF3366" });
  }).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.slug || form.slug.length < 3) { setDisp(null); return; }
    const t = setTimeout(() => api.get("/neni/tienda/slug-disponible", { params: { slug: form.slug } }).then(r => setDisp(r.data)).catch(() => {}), 350);
    return () => clearTimeout(t);
  }, [form.slug]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: k === "slug" ? v.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 30) : v }));
  const seleccionados = useMemo(() => new Set((data?.productos || []).map(p => p.id)), [data]);

  const guardar = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put("/neni/tienda", form); toast.success("¡Tu tienda online está lista!"); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const toggle = async (p) => {
    if (!data?.configurada) { toast.error("Primero guarda el nombre de tu tienda"); return; }
    try {
      if (seleccionados.has(p.id)) { await api.delete(`/neni/tienda/productos/${p.id}`); toast.success("Quitado de tu tienda"); }
      else { await api.post("/neni/tienda/productos", { producto_id: p.id, precio_venta: Math.round((p.precio_base_nenis || p.precio) * 1.2 * 100) / 100 }); toast.success(`${p.nombre} agregado a tu tienda`); }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cambiarPrecio = async (p, precio) => {
    try { await api.post("/neni/tienda/productos", { producto_id: p.id, precio_venta: parseFloat(precio) }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const url = data?.tienda?.slug ? `${window.location.origin}/${data.tienda.slug}` : "";
  const expo = (catalogo || []).filter(p => !q || p.nombre.toLowerCase().includes(q.toLowerCase()) || (p.tienda?.nombre_local || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="neni-tienda-tab">
      <div className="bg-zinc-900 text-white rounded-sm p-6 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <div className="overline text-[#D4FF00] flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> Mi tienda online</div>
          <div className="font-display font-black text-3xl tracking-tighter mt-1">Tu propio e-commerce, con tu nombre</div>
          <ol className="mt-3 grid sm:grid-cols-2 gap-2 text-sm">{PASOS.map((p, i) => <li key={`np-${i}`} className="flex gap-2 bg-white/5 rounded-sm p-2.5"><span className="font-display font-black text-[#FF3366]">{i + 1}</span><span className="text-white/85">{p}</span></li>)}</ol>
        </div>
        {url && (
          <div className="bg-white/10 rounded-sm p-4 text-center min-w-[260px]">
            <div className="text-[10px] uppercase tracking-wider text-white/60">Tu dirección</div>
            <div className="font-mono font-bold text-sm mt-1 break-all" data-testid="neni-url">{base}/{data.tienda.slug}</div>
            <div className="flex gap-2 mt-3 justify-center">
              <Button size="sm" data-testid="neni-copiar-url" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copiado"); }} className="rounded-sm bg-[#FF3366] hover:bg-[#E62958]"><Copy className="w-3.5 h-3.5 mr-1" /> Copiar</Button>
              <a href={url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-sm bg-transparent border-white/30 text-white hover:bg-white hover:text-zinc-900"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Ver</Button></a>
            </div>
            <div className="text-[10px] text-white/50 mt-2">{data.productos.length} artículos publicados</div>
          </div>
        )}
      </div>

      <form onSubmit={guardar} className="bg-white border border-zinc-200 rounded-sm p-5 grid md:grid-cols-2 gap-4" data-testid="neni-tienda-form">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Nombre de tu dirección (sub-dominio)</Label>
          <div className="mt-1 flex items-center border border-zinc-300 rounded-sm overflow-hidden focus-within:border-zinc-900">
            <span className="px-3 text-sm text-zinc-500 bg-zinc-50 h-11 grid place-items-center border-r border-zinc-200 whitespace-nowrap">{base}/</span>
            <input data-testid="neni-slug" value={form.slug} onChange={e => set("slug", e.target.value)} placeholder="mi-tienda-neni" required className="flex-1 h-11 px-3 font-mono text-sm outline-none" />
            {disp && <span data-testid="neni-slug-estado" className={`px-3 text-xs flex items-center gap-1 ${disp.disponible ? "text-emerald-600" : "text-red-600"}`}>{disp.disponible ? <><CheckCircle2 className="w-3.5 h-3.5" /> Disponible</> : <><XCircle className="w-3.5 h-3.5" /> {disp.motivo}</>}</span>}
          </div>
        </div>
        <div><Label className="text-xs uppercase tracking-wider text-zinc-500">Nombre visible de la tienda</Label><Input data-testid="neni-nombre-tienda" value={form.nombre_tienda} onChange={e => set("nombre_tienda", e.target.value)} required placeholder="Ej. Closet de Lupita" className="mt-1 rounded-sm h-11" /></div>
        <div><Label className="text-xs uppercase tracking-wider text-zinc-500">WhatsApp para pedidos</Label><Input data-testid="neni-whatsapp" value={form.whatsapp} onChange={e => set("whatsapp", e.target.value)} placeholder="10 dígitos" className="mt-1 rounded-sm h-11" /></div>
        <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wider text-zinc-500">Descripción</Label><Input data-testid="neni-descripcion" value={form.descripcion} onChange={e => set("descripcion", e.target.value)} placeholder="Moda, belleza y más con entrega en tu zona" className="mt-1 rounded-sm h-11" /></div>
        <div className="flex items-center gap-3"><Label className="text-xs uppercase tracking-wider text-zinc-500">Color</Label><input data-testid="neni-color" type="color" value={form.color} onChange={e => set("color", e.target.value)} className="w-10 h-10 rounded-sm border border-zinc-300" /></div>
        <Button type="submit" data-testid="neni-guardar-tienda" disabled={saving || (disp && !disp.disponible)} className="h-11 rounded-sm bg-zinc-900 hover:bg-[#FF3366] md:justify-self-end px-8">{saving ? "Guardando…" : data?.configurada ? "Guardar cambios" : "Crear mi tienda"}</Button>
      </form>

      {data?.productos?.length > 0 && (
        <section>
          <div className="overline text-zinc-500 mb-3">En mi tienda ({data.productos.length})</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.productos.map(p => (
              <div key={p.id} data-testid={`neni-sel-${p.id}`} className="bg-white border border-zinc-200 rounded-sm p-3 flex gap-3">
                <img src={p.imagen_url} alt={p.nombre} className="w-16 h-16 object-cover rounded-sm bg-zinc-100" />
                <div className="flex-1 min-w-0 text-sm">
                  <div className="font-display font-semibold truncate">{p.nombre}</div>
                  <div className="text-xs text-zinc-500">Base ${p.precio_base.toFixed(2)} · {p.tienda?.nombre_local}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-zinc-500">Vendo en $</span>
                    <input data-testid={`neni-precio-${p.id}`} type="number" step="1" min={p.precio_base} defaultValue={p.precio_venta} onBlur={e => e.target.value !== String(p.precio_venta) && cambiarPrecio(p, e.target.value)} className="w-24 h-8 border border-zinc-300 rounded-sm px-2 text-sm" />
                    <span className="text-xs text-emerald-700 font-medium">+${(p.precio_venta - p.precio_base).toFixed(2)}</span>
                  </div>
                </div>
                <button onClick={() => toggle(p)} data-testid={`neni-quitar-${p.id}`} className="text-zinc-400 hover:text-red-600 self-start"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
          <div><div className="overline text-zinc-500 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Exposición de productos</div><div className="text-sm text-zinc-600">Haz clic en la imagen para agregarla a tu tienda. Clic otra vez para quitarla.</div></div>
          <div className="relative w-64"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" /><Input data-testid="neni-expo-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar…" className="pl-9 h-10 rounded-sm" /></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
          {expo.map(p => {
            const sel = seleccionados.has(p.id);
            return (
              <button key={p.id} type="button" data-testid={`neni-expo-${p.id}`} onClick={() => toggle(p)} className={`group text-left rounded-sm overflow-hidden border-2 transition-all hover:-translate-y-0.5 ${sel ? "border-[#FF3366] ring-2 ring-[#FF3366]/30" : "border-zinc-200 hover:border-zinc-900"}`}>
                <div className="aspect-square bg-zinc-100 relative">
                  {p.imagen_url && <img src={p.imagen_url} alt={p.nombre} className="w-full h-full object-cover" />}
                  <div className={`absolute top-2 right-2 w-7 h-7 rounded-full grid place-items-center text-white ${sel ? "bg-[#FF3366]" : "bg-zinc-900/70 group-hover:bg-zinc-900"}`}>{sel ? <CheckCircle2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}</div>
                </div>
                <div className="p-2 bg-white"><div className="text-xs font-semibold line-clamp-1">{p.nombre}</div><div className="text-[11px] text-zinc-500">Base ${(p.precio_base_nenis || p.precio).toFixed(2)} · {p.tienda?.nombre_local}</div></div>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
};
